/**
 * Axios-shaped `DomainErrorResponse` rejections for mock API clients.
 *
 * A mock that only ever resolves hides every error path the UI has to handle.
 * Rejecting with this class means `getApiErrorMessage` (services/apiErrors.ts)
 * and the axios interceptor in services/api.ts read the failure through
 * exactly the same branch they take against the live backend — the backend's
 * own `error.message` reaches the user in mock mode too.
 *
 * Extends `Error` so stack traces and React Query error boundaries behave,
 * while carrying `isAxiosError` + `response` so the shared readers recognise
 * it as an HTTP failure rather than falling back to `error.message`.
 */
import type {
  DomainErrorDetail,
  DomainErrorResponse,
} from "./dataStudio/dataStudioAssets.types";

export interface MockDomainErrorOptions {
  status: number;
  /** e.g. `NOT_FOUND`, `VALIDATION_ERROR` — mirrors the backend's codes. */
  code: string;
  message: string;
  /** Only the backend's validation 422s carry this. */
  details?: unknown;
}

export class MockDomainError extends Error {
  readonly isAxiosError = true;

  readonly response: { status: number; data: DomainErrorResponse };

  constructor({ status, code, message, details }: MockDomainErrorOptions) {
    super(message);
    this.name = "MockDomainError";
    const error: DomainErrorDetail = {
      code,
      message,
      correlation_id: `mock-${code.toLowerCase()}-${status}`,
      ...(details === undefined ? {} : { details }),
    };
    this.response = { status, data: { success: false, error } };
  }
}

/** `404` — connector connection, asset, or column not found. */
export const mockNotFoundError = (message: string): MockDomainError =>
  new MockDomainError({ status: 404, code: "NOT_FOUND", message });

/**
 * `400` — the asset is not a tabular type, so it has no columns or rows.
 * Returned by the columns and rows endpoints for e.g. a file or BI chart.
 */
export const mockNotTabularError = (message: string): MockDomainError =>
  new MockDomainError({ status: 400, code: "VALIDATION_ERROR", message });

/** `400` — a curation PATCH body with no recognized fields to update. */
export const mockEmptyUpdateError = (message: string): MockDomainError =>
  new MockDomainError({ status: 400, code: "VALIDATION_ERROR", message });

/** `403` — the caller is signed in but may not make this change (a viewer writing). */
export const mockForbiddenError = (message: string): MockDomainError =>
  new MockDomainError({ status: 403, code: "FORBIDDEN", message });

/** `409` — the request is valid but conflicts with the resource's state. */
export const mockConflictError = (message: string): MockDomainError =>
  new MockDomainError({ status: 409, code: "CONFLICT", message });

/** `422` — request query/body validation, the only shape carrying `details`. */
export const mockValidationError = (
  message: string,
  details?: unknown,
): MockDomainError =>
  new MockDomainError({
    status: 422,
    code: "VALIDATION_ERROR",
    message,
    details,
  });
